import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import { toast } from "sonner";
import {
  X, CheckCircle, Plus, Trash, ArrowsClockwise, FolderSimple, Warning,
  DotsSixVertical, HardHat, CaretRight,
} from "@phosphor-icons/react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { PROJECT_STATUSES } from "../../data/mockProjects";
import { getMsStatus } from "../../api/microsoft";
import { createProject, updateProject, importFromOneDrive } from "../../api/projects";
import {
  createFolderSchema,
  deleteFolderSchema,
  getFolderSchema,
  getFolderSchemaDefaults,
  listFolderSchemas,
  suggestAndPrepareFolderSchema,
} from "../../api/folderSchemas";
import { FolderTreePreview, LayoutBadges } from "../settings/FolderLayoutPreview";
import { buildDefaultContractors } from "../../features/projects/contractors/model";
import SourceFolderPicker from "../files/SourceFolderPicker";

// ─── Tag / Multi-value input ─────────────────────────────────────────────────

const MultiValueInput = forwardRef(function MultiValueInput(
  { label, values, onChange, placeholder, type = "text", testIdPrefix }, ref
) {
  const [draft, setDraft] = useState("");

  const add = (v) => {
    const trimmed = (v ?? draft).trim();
    if (trimmed && !values.includes(trimmed)) onChange([...values, trimmed]);
    setDraft("");
  };

  // Exposed to the parent so it can flush any unconfirmed text on submit.
  useImperativeHandle(ref, () => ({
    commitDraft() {
      const trimmed = draft.trim();
      if (!trimmed) return values;
      if (values.includes(trimmed)) {
        setDraft("");
        return values;
      }
      const next = [...values, trimmed];
      onChange(next);
      setDraft("");
      return next;
    },
  }), [draft, values, onChange]);

  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
        {label}
      </Label>
      {values.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-1">
          {values.map((v) => (
            <span
              key={v}
              className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 border border-slate-200 rounded-sm text-xs text-slate-700"
            >
              {v}
              <button
                type="button"
                onClick={() => onChange(values.filter((x) => x !== v))}
                className="text-slate-400 hover:text-red-500 transition-colors"
                data-testid={`remove-${testIdPrefix}-${v}`}
              >
                <X size={10} />
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <Input
          type={type}
          placeholder={placeholder}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          className="flex-1 h-8 text-sm"
          data-testid={`${testIdPrefix}-input`}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => add()}
          disabled={!draft.trim()}
          className="h-8 px-2.5"
          data-testid={`${testIdPrefix}-add-btn`}
        >
          <Plus size={13} />
        </Button>
      </div>
    </div>
  );
});

// ─── Cost Breakdown Editor ────────────────────────────────────────────────────

const COST_TAG_SUGGESTIONS = [
  "Materials", "Labour", "Subcontractor", "Equipment", "Permits & Fees",
  "Engineering", "Travel", "Software", "Other",
];

function CostBreakdownEditor({ items, onChange }) {
  const [dragIndex, setDragIndex] = useState(null);
  const [dropIndex, setDropIndex] = useState(null);

  const total = (items || []).reduce((sum, c) => {
    const n = parseFloat(c.amount);
    return sum + (Number.isFinite(n) ? n : 0);
  }, 0);

  const addRow = () => onChange([...(items || []), { label: "", amount: "", notes: "", tag: "" }]);
  const updateRow = (i, field, value) => {
    const next = [...items];
    next[i] = { ...next[i], [field]: value };
    onChange(next);
  };
  const removeRow = (i) => onChange(items.filter((_, idx) => idx !== i));

  const handleDragStart = (i) => (e) => {
    setDragIndex(i);
    e.dataTransfer.effectAllowed = "move";
    // Required by Firefox to initiate a drag
    e.dataTransfer.setData("text/plain", String(i));
  };
  const handleDragOver = (i) => (e) => {
    e.preventDefault();
    if (dropIndex !== i) setDropIndex(i);
  };
  const handleDragLeave = () => setDropIndex(null);
  const handleDrop = (i) => (e) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === i) {
      setDragIndex(null);
      setDropIndex(null);
      return;
    }
    const next = [...items];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(i, 0, moved);
    onChange(next);
    setDragIndex(null);
    setDropIndex(null);
  };
  const handleDragEnd = () => {
    setDragIndex(null);
    setDropIndex(null);
  };

  return (
    <div className="space-y-2 pt-1" data-testid="cost-breakdown-editor">
      <div className="flex items-center justify-between">
        <Label className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
          Cost Breakdown (AUD)
        </Label>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addRow}
          className="h-7 px-2 text-xs"
          data-testid="cost-add-row"
        >
          <Plus size={12} className="mr-1" /> Add line item
        </Button>
      </div>

      {/* Shared datalist for tag autocomplete */}
      <datalist id="cost-tag-suggestions">
        {COST_TAG_SUGGESTIONS.map((t) => <option key={t} value={t} />)}
      </datalist>

      {(items || []).length === 0 ? (
        <p className="text-xs text-slate-400 italic">{'No cost items yet. Click "Add line item" to break down project costs (e.g. materials, labour, subcontractors).'}</p>
      ) : (
        <div className="space-y-1.5">
          {items.map((row, i) => {
            const isDragging = dragIndex === i;
            const isDropTarget = dropIndex === i && dragIndex !== null && dragIndex !== i;
            return (
              <div
                key={i}
                draggable
                onDragStart={handleDragStart(i)}
                onDragOver={handleDragOver(i)}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop(i)}
                onDragEnd={handleDragEnd}
                className={`grid grid-cols-12 gap-2 items-center transition-all ${
                  isDragging ? "opacity-40" : ""
                } ${isDropTarget ? "border-t-2 border-primary pt-1" : ""}`}
                data-testid={`cost-row-${i}`}
              >
                <div
                  className="col-span-1 flex items-center justify-center text-slate-300 hover:text-slate-500 cursor-grab active:cursor-grabbing"
                  title="Drag to reorder"
                  data-testid={`cost-drag-handle-${i}`}
                >
                  <DotsSixVertical size={14} weight="bold" />
                </div>
                <Input
                  placeholder="Label (e.g. Steel beams)"
                  value={row.label}
                  onChange={(e) => updateRow(i, "label", e.target.value)}
                  className="col-span-3 h-8 text-sm"
                  data-testid={`cost-label-${i}`}
                />
                <Input
                  list="cost-tag-suggestions"
                  placeholder="Tag"
                  value={row.tag || ""}
                  onChange={(e) => updateRow(i, "tag", e.target.value)}
                  className="col-span-2 h-8 text-sm"
                  data-testid={`cost-tag-${i}`}
                />
                <div className="col-span-2 relative">
                  <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs pointer-events-none">$</span>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={row.amount}
                    onChange={(e) => updateRow(i, "amount", e.target.value)}
                    onWheel={(e) => e.target.blur()}
                    className="pl-6 h-8 text-sm"
                    data-testid={`cost-amount-${i}`}
                  />
                </div>
                <Input
                  placeholder="Notes (optional)"
                  value={row.notes}
                  onChange={(e) => updateRow(i, "notes", e.target.value)}
                  className="col-span-3 h-8 text-sm"
                  data-testid={`cost-notes-${i}`}
                />
                <button
                  type="button"
                  onClick={() => removeRow(i)}
                  className="col-span-1 h-8 flex items-center justify-center text-slate-400 hover:text-red-500 transition-colors"
                  title="Remove line item"
                  data-testid={`cost-remove-${i}`}
                >
                  <Trash size={13} />
                </button>
              </div>
            );
          })}
          <div className="flex items-center justify-end gap-2 pt-2 mt-1 border-t border-slate-200">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Total Costs:</span>
            <span className="text-sm font-bold text-slate-900" data-testid="cost-total">
              {new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" }).format(total)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Helper: add a custom contractor type in the modal ───────────────────────
function ModalAddContractor({ onAdd, existing }) {
  const [draft, setDraft] = useState("");
  const add = () => {
    const t = draft.trim();
    if (!t || existing.some((e) => e.toLowerCase() === t.toLowerCase())) return;
    onAdd(t);
    setDraft("");
  };
  return (
    <div className="flex items-center gap-2">
      <input
        type="text"
        placeholder="Add contractor type (e.g. Civil)"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
        className="flex-1 h-8 px-3 text-xs border border-slate-200 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-primary/40"
        data-testid="modal-new-contractor-type-input"
      />
      <button
        type="button"
        onClick={add}
        disabled={!draft.trim()}
        className="flex items-center gap-1 h-8 px-3 text-xs font-medium text-primary border border-primary/30 rounded-md hover:bg-primary/5 disabled:opacity-40 transition-colors"
        data-testid="modal-add-custom-contractor-btn"
      >
        <Plus size={11} />
        Add
      </button>
    </div>
  );
}

const ROUTING_NOTE_TYPES = [
  ["alias", "Alias"], ["job_code", "Job code"],
  ["drawing_prefix", "Drawing prefix"], ["positive", "Positive instruction"],
  ["negative", "Negative instruction"], ["exception", "Learned exception"],
];

function InitialRoutingNotesEditor({ notes, onChange }) {
  const [expanded, setExpanded] = useState(false);
  const [draft, setDraft] = useState({ note_type: "alias", content: "", effect: "negative" });
  const add = () => {
    const content = draft.content.trim();
    if (!content || notes.length >= 20) return;
    const candidate = {
      note_type: draft.note_type,
      content,
      ...(draft.note_type === "exception" ? { effect: draft.effect } : {}),
    };
    const duplicate = notes.some((n) => n.note_type === candidate.note_type
      && n.content.toLowerCase() === content.toLowerCase()
      && (n.effect || null) === (candidate.effect || null));
    if (!duplicate) onChange([...notes, candidate]);
    setDraft((current) => ({ ...current, content: "" }));
  };
  return (
    <section className="border border-slate-200 rounded-md overflow-hidden" data-testid="initial-routing-notes-editor">
      <button type="button" aria-expanded={expanded} onClick={() => setExpanded((value) => !value)} className="w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-slate-50 transition-colors" data-testid="initial-routing-notes-toggle">
        <CaretRight size={11} className={`text-slate-400 transition-transform ${expanded ? "rotate-90" : ""}`} />
        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Email routing notes</span>
        {notes.length > 0 && <span className="text-[10px] text-slate-400">{notes.length}</span>}
        <span className="ml-auto text-[10px] text-slate-400">Optional</span>
      </button>
      {expanded && <div className="space-y-3 border-t border-slate-100 px-3 py-3">
      <p className="text-[11px] text-slate-400">Add project names, codes or exceptions. They guide matching but never override review safeguards.</p>
      {notes.length > 0 && <div className="space-y-1.5">
        {notes.map((note, index) => <div key={`${note.note_type}-${index}`} className="flex items-start gap-2 rounded border border-slate-200 bg-slate-50 px-2.5 py-2 text-xs">
          <span className="rounded bg-blue-50 border border-blue-100 px-1.5 py-0.5 text-[9px] font-semibold text-blue-700">Added by you</span>
          <span className="font-medium text-slate-500">{ROUTING_NOTE_TYPES.find(([v]) => v === note.note_type)?.[1]}</span>
          <span className="flex-1 text-slate-700 break-words">{note.content}</span>
          <button type="button" aria-label={`Remove routing note ${index + 1}`} onClick={() => onChange(notes.filter((_, i) => i !== index))} className="text-slate-400 hover:text-red-600"><Trash size={12} /></button>
        </div>)}
      </div>}
      <div className="grid grid-cols-1 sm:grid-cols-[150px_1fr_auto] gap-2">
        <select aria-label="Routing note type" value={draft.note_type} onChange={(e) => setDraft({ ...draft, note_type: e.target.value })} className="h-9 rounded-md border border-slate-200 bg-white px-2 text-xs">
          {ROUTING_NOTE_TYPES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
        <input aria-label="Routing note content" maxLength={500} value={draft.content} onChange={(e) => setDraft({ ...draft, content: e.target.value })} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} placeholder="e.g. Job code DF-204" className="h-9 rounded-md border border-slate-200 px-3 text-sm" />
        <Button type="button" variant="outline" onClick={add} disabled={!draft.content.trim() || notes.length >= 20} className="h-9 text-xs"><Plus size={12} className="mr-1" /> Add</Button>
      </div>
      {draft.note_type === "exception" && <div className="flex items-center gap-2 text-xs"><Label>Effect</Label><select value={draft.effect} onChange={(e) => setDraft({ ...draft, effect: e.target.value })} className="h-8 rounded border border-slate-200 bg-white px-2"><option value="negative">Negative</option><option value="positive">Positive</option></select></div>}
      <p className="text-[10px] text-slate-400 text-right">{notes.length}/20 notes · {draft.content.length}/500 characters</p>
      </div>}
    </section>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

const EMPTY_FORM = {
  name: "",
  description: "",
  status: "active",
  project_location: [],
  invoice_number: "",
  po_number: "",
  quoted_price: "",
  final_invoice_amount: "",
  clients: [],
  client_emails: [],
  contractors: buildDefaultContractors(),
  primary_contact: "",
  phone: "",
  tags: [],
  notes: "",
  routing_notes: [],
  folder_schema_id: null,
};

/**
 * NewProjectModal
 * Props:
 *   onClose()              — dismiss without saving
 *   onSave(project)        — called with the saved ProjectResponse
 *   initialData?           — if provided, runs in edit mode (PUT instead of POST)
 */
export default function NewProjectModal({ onClose, onSave, initialData = null }) {
  const isEdit = Boolean(initialData);

  const [form, setForm] = useState(isEdit ? {
    name:             initialData.name ?? "",
    description:      initialData.description ?? "",
    status:           initialData.status ?? "active",
    project_location: Array.isArray(initialData.project_location)
                        ? initialData.project_location
                        : (initialData.project_location ? [initialData.project_location] : []),
    invoice_number:   initialData.invoice_number ?? "",
    po_number:        initialData.po_number ?? "",
    quoted_price:     initialData.quoted_price == null ? "" : String(initialData.quoted_price),
    final_invoice_amount: initialData.final_invoice_amount == null ? "" : String(initialData.final_invoice_amount),
    costs:            (initialData.costs || []).map((c) => ({
                        label:  c.label  ?? "",
                        amount: c.amount == null ? "" : String(c.amount),
                        notes:  c.notes  ?? "",
                        tag:    c.tag    ?? "",
                      })),
    clients:          initialData.clients ?? [],
    client_emails:    initialData.client_emails ?? [],
    primary_contact:  initialData.primary_contact ?? "",
    phone:            initialData.phone ?? "",
    tags:             initialData.tags ?? [],
    notes:            initialData.notes ?? "",
    routing_notes:    [],
    folder_schema_id: initialData.folder_schema_id ?? null,
    contractors:      initialData.contractors?.length > 0
                        ? initialData.contractors
                        : buildDefaultContractors(),
  } : { ...EMPTY_FORM });

  const [errors, setErrors] = useState({});
  const [savingState, setSavingState] = useState("idle");
  const [folderWarning, setFolderWarning] = useState(null);
  const [msConnected, setMsConnected] = useState(false);

  // ── OneDrive import state (create mode only) ────────────────────────────
  const [importEnabled,    setImportEnabled]    = useState(false);
  const [importFolders,    setImportFolders]    = useState([]); // [{id, name}]
  const [showFolderPicker, setShowFolderPicker] = useState(false);

  // ── Folder layout selection + AI recommendation ─────────────────────────
  const [folderSchemas, setFolderSchemas] = useState([]);
  const [folderSchemaDefaults, setFolderSchemaDefaults] = useState(null);
  const [selectedSchemaDetail, setSelectedSchemaDetail] = useState(null);
  const [layoutLoading, setLayoutLoading] = useState(!isEdit);
  const [layoutPreviewOpen, setLayoutPreviewOpen] = useState(false);
  const [aiLayoutAnalysis, setAiLayoutAnalysis] = useState(null);
  const [aiLayoutSource, setAiLayoutSource] = useState(null);
  const [aiLayoutAnalyzing, setAiLayoutAnalyzing] = useState(false);
  const [pendingAiSchema, setPendingAiSchema] = useState(null);

  // Refs so we can flush any unconfirmed text in the chip-style inputs at submit time.
  const clientsInputRef  = useRef(null);
  const emailsInputRef   = useRef(null);
  const tagsInputRef     = useRef(null);
  const locationInputRef = useRef(null);

  useEffect(() => {
    getMsStatus().then(({ connected }) => setMsConnected(connected)).catch(() => {});
  }, []);

  useEffect(() => {
    if (isEdit) return;
    let active = true;
    Promise.all([listFolderSchemas(), getFolderSchemaDefaults()])
      .then(([schemas, defaults]) => {
        if (!active) return;
        setFolderSchemas(schemas);
        setFolderSchemaDefaults(defaults);
        const effective = defaults.effective_default || schemas[0]?.id || null;
        setForm((current) => ({
          ...current,
          folder_schema_id: current.folder_schema_id || effective,
        }));
      })
      .catch(() => toast.error("Could not load folder layouts. The system default will be used."))
      .finally(() => { if (active) setLayoutLoading(false); });
    return () => { active = false; };
  }, [isEdit]);

  useEffect(() => {
    if (!form.folder_schema_id || form.folder_schema_id === "__ai_custom__") {
      setSelectedSchemaDetail(pendingAiSchema);
      return;
    }
    let active = true;
    getFolderSchema(form.folder_schema_id)
      .then((schema) => { if (active) setSelectedSchemaDetail(schema); })
      .catch(() => { if (active) setSelectedSchemaDetail(null); });
    return () => { active = false; };
  }, [form.folder_schema_id, pendingAiSchema]);

  const analyzeImportFolder = async (folder) => {
    setAiLayoutSource(folder);
    setAiLayoutAnalyzing(true);
    setAiLayoutAnalysis(null);
    try {
      const result = await suggestAndPrepareFolderSchema(
        folder.id,
        form.name.trim() || folder.name || "Imported Project",
      );
      setAiLayoutAnalysis(result);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "AI could not analyze this folder. You can keep your selected layout.");
    } finally {
      setAiLayoutAnalyzing(false);
    }
  };

  const chooseFolderSchema = (schemaId) => {
    setPendingAiSchema(null);
    setAiLayoutAnalysis(null);
    setForm((current) => ({ ...current, folder_schema_id: schemaId }));
  };

  const useAiRecommendation = () => {
    const ready = aiLayoutAnalysis?.ready_to_use;
    if (!ready) return;
    if (ready.custom_schema) {
      const custom = {
        ...ready.custom_schema,
        name: `AI Layout — ${form.name.trim() || aiLayoutSource?.name || "Imported Project"}`,
        description: `${ready.custom_schema.description || "AI-customized layout"} Based on “${aiLayoutSource?.name || "selected OneDrive folder"}”.`,
      };
      setPendingAiSchema(custom);
      setForm((current) => ({ ...current, folder_schema_id: "__ai_custom__" }));
    } else if (ready.schema_id) {
      setPendingAiSchema(null);
      setForm((current) => ({ ...current, folder_schema_id: ready.schema_id }));
    }
    toast.success("AI folder layout recommendation selected.");
  };

  const set = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    if (errors[key]) setErrors((err) => ({ ...err, [key]: null }));
  };
  const setList = (key) => (val) => setForm((f) => ({ ...f, [key]: val }));

  /** Flush any draft text in the chip-style inputs so a user who typed but
   *  didn't press Enter / click + still gets that value committed.  */
  const flushChipDrafts = () => ({
    clients:          clientsInputRef.current?.commitDraft?.()   ?? form.clients,
    client_emails:    emailsInputRef.current?.commitDraft?.()    ?? form.client_emails,
    tags:             tagsInputRef.current?.commitDraft?.()      ?? form.tags,
    project_location: locationInputRef.current?.commitDraft?.()  ?? form.project_location,
  });

  const validate = (flushed) => {
    const e = {};
    if (!form.name.trim()) e.name = "Project name is required";
    if ((flushed?.clients ?? form.clients).length === 0) e.clients = "Add at least one client";
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const flushed = flushChipDrafts();
    const errs = validate(flushed);
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setFolderWarning(null);
    setSavingState("saving");
    let createdAiSchemaId = null;
    try {
      let resolvedFolderSchemaId = form.folder_schema_id;
      if (resolvedFolderSchemaId === "__ai_custom__" && pendingAiSchema) {
        const createdLayout = await createFolderSchema({
          name: pendingAiSchema.name,
          description: pendingAiSchema.description,
          folders: pendingAiSchema.folders || [],
          archive_strategy: pendingAiSchema.archive_strategy || "flat_archive",
          default_contractors: pendingAiSchema.default_contractors || null,
          auto_folders: pendingAiSchema.auto_folders || null,
        });
        createdAiSchemaId = createdLayout.id;
        resolvedFolderSchemaId = createdLayout.id;
      }
      const toFloat = (v) => {
        if (v === "" || v === null || v === undefined) return null;
        const n = parseFloat(v);
        return Number.isFinite(n) ? n : null;
      };
      const cleanedCosts = (form.costs || [])
        .map((c) => ({
          label: (c.label || "").trim(),
          amount: toFloat(c.amount) ?? 0,
          notes: (c.notes || "").trim(),
          tag:   (c.tag   || "").trim(),
        }))
        // Keep any row the user touched (label / amount / notes / tag).
        // Only fully-blank rows added via "Add line item" but never filled
        // in are dropped.
        .filter((c) => c.label || c.amount || c.notes || c.tag);
      const payload = {
        ...form,
        quoted_price:         toFloat(form.quoted_price),
        final_invoice_amount: toFloat(form.final_invoice_amount),
        costs:                cleanedCosts,
        clients:              flushed.clients,
        client_emails:        flushed.client_emails,
        tags:                 flushed.tags,
        contractors:          form.contractors,
        folder_schema_id:     resolvedFolderSchemaId || null,
        // OneDrive folder fields are passed through on edit only; on create
        // the backend auto-creates the folder via MS Graph and writes the IDs
        // server-side, so we deliberately do NOT send them from the form.
        ...(isEdit ? {
          onedrive_folder_id:   initialData?.onedrive_folder_id ?? null,
          onedrive_folder_url:  initialData?.onedrive_folder_url ?? null,
          onedrive_folder_name: initialData?.onedrive_folder_name ?? null,
        } : {}),
      };
      if (isEdit) delete payload.routing_notes;
      const saved = isEdit
        ? await updateProject(initialData.id, payload)
        : await createProject(payload);

      // Backend signals OneDrive folder creation failures via folder_warning
      // on the create response. Show it inline rather than blocking save.
      if (!isEdit && saved?.folder_warning) {
        setFolderWarning(saved.folder_warning);
        toast.warning("Project created, but the OneDrive folder could not be set up.");
      } else if (!isEdit && importEnabled && importFolders.length > 0 && saved?.id) {
        // Trigger background import — fire and forget, user gets an in-app notification when done
        try {
          await importFromOneDrive(saved.id, importFolders.map((f) => f.id));
        } catch (importErr) {
          console.warn("Import trigger failed:", importErr);
        }
        toast.success(
          `Project created. Importing files from ${importFolders.length} folder(s) in the background — you'll get a notification when it's done.`,
          { duration: 6000 }
        );
      } else {
        toast.success(isEdit ? "Project updated." : "Project created.");
      }
      onSave(saved);
    } catch (err) {
      if (createdAiSchemaId) {
        try { await deleteFolderSchema(createdAiSchemaId); } catch (_) { /* best-effort orphan cleanup */ }
      }
      toast.error(err.response?.data?.detail || "Failed to save project.");
    } finally {
      setSavingState("idle");
    }
  };

  const isSaving = savingState !== "idle";
  const selectedSchemaSummary = form.folder_schema_id === "__ai_custom__"
    ? { id: "__ai_custom__", name: pendingAiSchema?.name || "AI-customized layout", description: pendingAiSchema?.description, scope: "AI recommendation" }
    : folderSchemas.find((schema) => schema.id === form.folder_schema_id);
  const selectedIsDefault = Boolean(
    form.folder_schema_id && form.folder_schema_id === folderSchemaDefaults?.effective_default
  );

  const saveLabel = () => {
    if (savingState === "saving") return isEdit ? "Saving…" : importEnabled && importFolders.length > 0 ? "Creating & queuing import…" : "Creating…";
    return isEdit ? "Save Changes" : "Create Project";
  };

  const selectClass = "w-full h-9 px-3 text-sm border border-slate-200 rounded-md bg-white text-slate-900 focus:outline-none focus:ring-1 focus:ring-primary";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      data-testid="new-project-modal"
    >
      <div className="absolute inset-0 bg-slate-900/50" onClick={onClose} />
      <div className="relative z-10 bg-white border border-slate-200 w-full max-w-2xl shadow-xl flex flex-col max-h-[90vh]">

        {/* ── Header ── */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 flex-shrink-0">
          <h2 className="font-cabinet text-base font-bold text-slate-900 tracking-tight">
            {isEdit ? `Edit — ${initialData.name}` : "New Project"}
          </h2>
          <button
            onClick={onClose}
            data-testid="close-modal-btn"
            className="text-slate-400 hover:text-slate-700 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* ── Scrollable Body ── */}
        <form onSubmit={handleSubmit} className="flex flex-col overflow-hidden">
          <div className="overflow-y-auto px-6 py-5 space-y-6">

            {/* OneDrive folder notice */}
            {!isEdit && (
              <div className={`flex items-start gap-2.5 p-3 rounded-sm border text-xs ${
                msConnected
                  ? "bg-blue-50 border-blue-200 text-blue-700"
                  : "bg-slate-50 border-slate-200 text-slate-500"
              }`}>
                {msConnected ? (
                  <FolderSimple size={14} className="text-[#0078D4] flex-shrink-0 mt-0.5" />
                ) : (
                  <Warning size={14} className="text-slate-400 flex-shrink-0 mt-0.5" />
                )}
                {msConnected
                  ? "A dedicated OneDrive folder will be auto-created inside \"DD Project Planner\" when you save."
                  : "Connect to OneDrive on the Explore Files page to auto-create a project folder. You can link it later."}
              </div>
            )}

            {/* ── Folder layout selection (create mode) ── */}
            {!isEdit && (
              <section className="rounded-md border border-blue-200 bg-blue-50/40 p-4 space-y-3" data-testid="new-project-folder-layout-section">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-xs font-bold uppercase tracking-wide text-slate-700">Folder Layout</h3>
                      {selectedIsDefault && <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[9px] font-bold uppercase text-emerald-700">Your default</span>}
                      {form.folder_schema_id === "__ai_custom__" && <span className="rounded-full bg-violet-100 px-2 py-0.5 text-[9px] font-bold uppercase text-violet-700">AI recommendation</span>}
                    </div>
                    <p className="mt-1 text-[11px] text-slate-500">This exact structure will be created inside the project&apos;s OneDrive folder. The default is preselected.</p>
                  </div>
                  <a href="/settings?tab=layouts" target="_blank" rel="noreferrer" className="flex-shrink-0 text-[10px] font-semibold text-primary hover:underline">Manage layouts</a>
                </div>
                {layoutLoading ? <p className="text-xs text-slate-400">Loading your default layout…</p> : <>
                  <select
                    data-testid="new-project-folder-layout-select"
                    value={form.folder_schema_id || ""}
                    onChange={(e) => chooseFolderSchema(e.target.value)}
                    className="w-full rounded-md border border-blue-200 bg-white px-3 py-2.5 text-sm font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-200"
                  >
                    {form.folder_schema_id === "__ai_custom__" && <option value="__ai_custom__">{pendingAiSchema?.name || "AI-customized layout"}</option>}
                    {folderSchemas.map((schema) => <option key={schema.id} value={schema.id}>{schema.name}{schema.id === folderSchemaDefaults?.effective_default ? " — Default" : ""} · {schema.scope}</option>)}
                  </select>
                  {selectedSchemaSummary && <div className="rounded-md border border-blue-100 bg-white px-3 py-2.5">
                    <div className="flex items-start justify-between gap-3"><div><p className="text-xs font-semibold text-slate-800">Using: {selectedSchemaSummary.name}</p><p className="mt-0.5 text-[11px] leading-relaxed text-slate-500">{selectedSchemaSummary.description || "No description provided."}</p></div>{form.folder_schema_id !== "__ai_custom__" && <LayoutBadges schema={selectedSchemaSummary} compact />}</div>
                    <button type="button" onClick={() => setLayoutPreviewOpen((value) => !value)} className="mt-2 text-[10px] font-semibold text-primary" data-testid="toggle-folder-layout-preview">{layoutPreviewOpen ? "Hide folder preview" : "Preview folders and guidance"}</button>
                    {layoutPreviewOpen && <div className="mt-3 max-h-72 overflow-y-auto pr-1"><FolderTreePreview schema={selectedSchemaDetail} /></div>}
                  </div>}
                </>}
              </section>
            )}

            {/* ── Import from existing OneDrive folder (create mode + ms connected) ── */}
            {!isEdit && msConnected && (
              <div className="rounded-sm border border-slate-200 bg-white overflow-hidden">
                {/* Toggle row */}
                <button
                  type="button"
                  onClick={() => setImportEnabled((v) => !v)}
                  className="flex items-center justify-between w-full px-4 py-3 hover:bg-slate-50 transition-colors group"
                  data-testid="import-onedrive-toggle"
                >
                  <div className="flex items-center gap-2.5">
                    <FolderSimple size={14} className="text-violet-500 flex-shrink-0" weight="fill" />
                    <div className="text-left">
                      <p className="text-xs font-semibold text-slate-800">Import files from existing OneDrive folder</p>
                      <p className="text-[10px] text-slate-400 mt-0.5">
                        AI will sort all files into the correct project subfolders
                      </p>
                    </div>
                  </div>
                  {/* Toggle pill */}
                  <div
                    className={`w-9 h-5 rounded-full transition-colors flex-shrink-0 ${importEnabled ? "bg-violet-600" : "bg-slate-200"}`}
                  >
                    <div
                      className={`w-4 h-4 bg-white rounded-full shadow-sm mt-0.5 transition-transform ${importEnabled ? "translate-x-4.5 ml-0.5" : "ml-0.5"}`}
                    />
                  </div>
                </button>

                {/* Expanded: folder list */}
                {importEnabled && (
                  <div className="border-t border-slate-100 px-4 py-3 space-y-2 bg-slate-50/50">
                    {importFolders.length > 0 && (
                      <ul className="space-y-1.5 mb-2" data-testid="import-folder-list">
                        {importFolders.map((f) => (
                          <li key={f.id} className="flex items-center gap-2 bg-white border border-slate-200 rounded px-3 py-1.5">
                            <FolderSimple size={12} className="text-amber-400 flex-shrink-0" weight="fill" />
                            <span className="text-xs text-slate-700 flex-1 truncate">{f.name}</span>
                            <button
                              type="button"
                              onClick={() => setImportFolders((prev) => prev.filter((x) => x.id !== f.id))}
                              className="text-slate-300 hover:text-red-400 transition-colors text-[11px]"
                              data-testid={`remove-import-folder-${f.name}`}
                            >
                              ✕
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                    <button
                      type="button"
                      onClick={() => setShowFolderPicker(true)}
                      className="flex items-center gap-1.5 text-xs font-semibold text-violet-600 hover:text-violet-800 border border-violet-200 hover:border-violet-400 bg-white px-3 py-1.5 rounded transition-colors w-full justify-center"
                      data-testid="add-import-folder-btn"
                    >
                      + Add source folder
                    </button>

                    {aiLayoutAnalyzing && <div className="flex items-center justify-center gap-2 rounded-md border border-violet-100 bg-violet-50 px-3 py-3 text-xs text-violet-700" data-testid="ai-layout-analyzing"><ArrowsClockwise size={13} className="animate-spin" />AI is comparing this folder to your layout presets…</div>}
                    {aiLayoutAnalysis && !aiLayoutAnalyzing && <div className="rounded-md border border-violet-200 bg-white p-3 space-y-2" data-testid="ai-folder-layout-recommendation">
                      <div className="flex items-start justify-between gap-3"><div><p className="text-[10px] font-bold uppercase tracking-wide text-violet-600">AI folder recommendation</p><p className="mt-1 text-xs font-semibold text-slate-800">{aiLayoutAnalysis.ready_to_use?.custom_schema?.name || aiLayoutAnalysis.suggestion?.recommended_preset?.name || "Recommended layout"}</p></div><span className="rounded-full bg-violet-100 px-2 py-0.5 text-[9px] font-bold text-violet-700">{Math.round((aiLayoutAnalysis.suggestion?.confidence || 0) * 100)}% match</span></div>
                      <p className="text-[11px] leading-relaxed text-slate-500">{aiLayoutAnalysis.suggestion?.reason}</p>
                      {(aiLayoutAnalysis.suggestion?.suggested_modifications || []).length > 0 && <div className="rounded bg-amber-50 px-2.5 py-2"><p className="text-[9px] font-bold uppercase text-amber-700">AI adjustments</p><ul className="mt-1 space-y-0.5 text-[10px] text-amber-800">{aiLayoutAnalysis.suggestion.suggested_modifications.map((item) => <li key={item}>• {item}</li>)}</ul></div>}
                      <button type="button" onClick={useAiRecommendation} data-testid="use-ai-folder-recommendation-btn" className="w-full rounded-md bg-violet-600 px-3 py-2 text-xs font-semibold text-white hover:bg-violet-700">Use AI recommendation</button>
                      <p className="text-center text-[9px] text-slate-400">Nothing changes until you click the button. You can switch layouts afterward.</p>
                    </div>}
                    <p className="text-[10px] text-slate-400 text-center">
                      The import runs in the background — you will get a notification when it is done.
                      Email backfill will not start until the import finishes.
                    </p>
                  </div>
                )}
              </div>
            )}

            {folderWarning && (
              <div className="flex items-start gap-2 bg-yellow-50 border border-yellow-200 p-3 rounded-sm">
                <Warning size={14} className="text-yellow-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-yellow-700">{folderWarning}</p>
              </div>
            )}

            {/* ─── Section: Project Details ─── */}
            <section className="space-y-4">
              <h3 className="text-[10px] font-bold uppercase tracking-widest text-slate-400 border-b border-slate-100 pb-1.5">
                Project Details
              </h3>

              {/* Name */}
              <div className="space-y-1.5">
                <Label htmlFor="proj-name" className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
                  Project Name *
                </Label>
                <Input
                  id="proj-name"
                  data-testid="new-project-name"
                  placeholder="e.g. Sydney CBD Office Tower"
                  value={form.name}
                  onChange={set("name")}
                  className={errors.name ? "border-red-400" : ""}
                />
                {errors.name && <p className="text-xs text-red-500">{errors.name}</p>}
              </div>

              {/* Description */}
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
                  Description
                </Label>
                <textarea
                  data-testid="new-project-description"
                  placeholder="Brief project overview, scope, key deliverables…"
                  value={form.description}
                  onChange={set("description")}
                  rows={3}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md bg-white text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-primary resize-none"
                />
              </div>

              {/* Status + Type */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
                    Status
                  </Label>
                  <select data-testid="new-project-status" value={form.status} onChange={set("status")} className={selectClass}>
                    {PROJECT_STATUSES.map((s) => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Location */}
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
                  Project Location(s)
                </Label>
                <MultiValueInput
                  ref={locationInputRef}
                  testIdPrefix="new-project-location"
                  placeholder="Add a location and press Enter…"
                  values={form.project_location}
                  onChange={setList("project_location")}
                />
              </div>

              {/* Invoice + PO */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
                    Invoice Number
                  </Label>
                  <Input
                    data-testid="new-project-invoice"
                    placeholder="e.g. INV-2025-001"
                    value={form.invoice_number}
                    onChange={set("invoice_number")}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
                    Purchase Order Number
                  </Label>
                  <Input
                    data-testid="new-project-po-number"
                    placeholder="e.g. PO-2025-042"
                    value={form.po_number}
                    onChange={set("po_number")}
                  />
                </div>
              </div>

              {/* Financials */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
                    Quoted Price (AUD)
                  </Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none">$</span>
                    <Input
                      data-testid="new-project-quoted-price"
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="e.g. 45000"
                      value={form.quoted_price}
                      onChange={set("quoted_price")}
                      onWheel={(e) => e.target.blur()}
                      className="pl-7"
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
                    Final Invoice Amount (AUD)
                  </Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none">$</span>
                    <Input
                      data-testid="new-project-final-invoice-amount"
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="leave blank until invoiced"
                      value={form.final_invoice_amount}
                      onChange={set("final_invoice_amount")}
                      className="pl-7"
                    />
                  </div>
                </div>
              </div>

              {/* Cost Breakdown */}
              <CostBreakdownEditor
                items={form.costs}
                onChange={(next) => setForm((f) => ({ ...f, costs: next }))}
              />
            </section>

            {/* ─── Section: Client Information ─── */}
            <section className="space-y-4">
              <h3 className="text-[10px] font-bold uppercase tracking-widest text-slate-400 border-b border-slate-100 pb-1.5">
                Client Information
              </h3>

              <MultiValueInput
                ref={clientsInputRef}
                label="Client Name(s) *"
                values={form.clients}
                onChange={setList("clients")}
                placeholder="e.g. Lendlease — press Enter or +"
                testIdPrefix="client"
              />
              {errors.clients && <p className="text-xs text-red-500 -mt-2">{errors.clients}</p>}

              <MultiValueInput
                ref={emailsInputRef}
                label="Client Email Address(es)"
                values={form.client_emails}
                onChange={setList("client_emails")}
                placeholder="e.g. projects@lendlease.com — press Enter or +"
                type="email"
                testIdPrefix="client-email"
              />

              {/* Primary Contact + Phone */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
                    Primary Contact Person
                  </Label>
                  <Input
                    data-testid="new-project-contact"
                    placeholder="e.g. Jane Smith"
                    value={form.primary_contact}
                    onChange={set("primary_contact")}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
                    Phone
                  </Label>
                  <Input
                    type="tel"
                    data-testid="new-project-phone"
                    placeholder="e.g. +61 2 9000 0000"
                    value={form.phone}
                    onChange={set("phone")}
                  />
                </div>
              </div>
            </section>

            {/* ─── Section: Contractors ─── */}
            <section className="space-y-3">
              <h3 className="text-[10px] font-bold uppercase tracking-widest text-slate-400 border-b border-slate-100 pb-1.5 flex items-center gap-1.5">
                <HardHat size={11} className="text-slate-400" />
                Contractors
              </h3>
              <p className="text-xs text-slate-500">
                Add contact details for each discipline. These emails are used by the AI to route incoming emails to this project.
              </p>
              <div className="space-y-2">
                {form.contractors.map((c, ci) => (
                  <div
                    key={`${c.type}-${ci}`}
                    className="border border-slate-200 rounded-sm p-3 space-y-2 bg-slate-50/50"
                    data-testid={`modal-contractor-${c.type}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-semibold text-slate-800">{c.type}</span>
                      <div className="flex items-center gap-3">
                        <label className="flex items-center gap-1.5 cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={c.not_needed}
                            onChange={() => {
                              const updated = [...form.contractors];
                              updated[ci] = { ...c, not_needed: !c.not_needed };
                              setForm((f) => ({ ...f, contractors: updated }));
                            }}
                            className="accent-slate-500 w-3.5 h-3.5"
                            data-testid={`modal-contractor-not-needed-${c.type}`}
                          />
                          <span className="text-[11px] text-slate-500">Not needed</span>
                        </label>
                        {!c.is_default && (
                          <button
                            type="button"
                            onClick={() => setForm((f) => ({ ...f, contractors: f.contractors.filter((_, i) => i !== ci) }))}
                            className="text-slate-400 hover:text-red-500 transition-colors"
                            data-testid={`modal-contractor-remove-${c.type}`}
                          >
                            <Trash size={12} />
                          </button>
                        )}
                      </div>
                    </div>
                    {!c.not_needed && (
                      <div className="pl-1 space-y-1.5">
                        {c.contacts.map((ct, ki) => (
                          <div key={ki} className="flex items-center gap-1.5">
                            <input
                              type="text"
                              placeholder="Name"
                              value={ct.name}
                              onChange={(e) => {
                                const updated = [...form.contractors];
                                const contacts = [...c.contacts];
                                contacts[ki] = { ...ct, name: e.target.value };
                                updated[ci] = { ...c, contacts };
                                setForm((f) => ({ ...f, contractors: updated }));
                              }}
                              className="w-32 h-7 px-2 text-xs border border-slate-200 rounded bg-white focus:outline-none focus:ring-1 focus:ring-primary/40"
                              data-testid={`modal-contractor-name-${c.type}-${ki}`}
                            />
                            <input
                              type="email"
                              placeholder="email@example.com"
                              value={ct.email}
                              onChange={(e) => {
                                const updated = [...form.contractors];
                                const contacts = [...c.contacts];
                                contacts[ki] = { ...ct, email: e.target.value };
                                updated[ci] = { ...c, contacts };
                                setForm((f) => ({ ...f, contractors: updated }));
                              }}
                              className="flex-1 h-7 px-2 text-xs border border-slate-200 rounded bg-white focus:outline-none focus:ring-1 focus:ring-primary/40"
                              data-testid={`modal-contractor-email-${c.type}-${ki}`}
                            />
                            <button
                              type="button"
                              onClick={() => {
                                const updated = [...form.contractors];
                                updated[ci] = { ...c, contacts: c.contacts.filter((_, i) => i !== ki) };
                                setForm((f) => ({ ...f, contractors: updated }));
                              }}
                              className="text-slate-400 hover:text-red-500 transition-colors"
                              data-testid={`modal-contractor-contact-remove-${c.type}-${ki}`}
                            >
                              <X size={11} />
                            </button>
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={() => {
                            const updated = [...form.contractors];
                            updated[ci] = { ...c, contacts: [...c.contacts, { name: "", email: "" }] };
                            setForm((f) => ({ ...f, contractors: updated }));
                          }}
                          className="flex items-center gap-1 text-[11px] text-slate-500 hover:text-primary transition-colors"
                          data-testid={`modal-contractor-add-contact-${c.type}`}
                        >
                          <Plus size={10} />
                          Add contact
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {/* Add custom contractor */}
              <ModalAddContractor
                onAdd={(type) =>
                  setForm((f) => ({
                    ...f,
                    contractors: [
                      ...f.contractors,
                      { type, is_default: false, not_needed: false, contacts: [] },
                    ],
                  }))
                }
                existing={form.contractors.map((c) => c.type)}
              />
            </section>

            {!isEdit ? (
              <InitialRoutingNotesEditor
                notes={form.routing_notes || []}
                onChange={(routing_notes) => setForm((f) => ({ ...f, routing_notes }))}
              />
            ) : (
              <p className="text-xs text-slate-400 border border-slate-200 rounded-md px-3 py-2">
                Manage email routing notes from the project detail page.
              </p>
            )}

            {/* ─── Section: Tags & Notes ─── */}
            <section className="space-y-4">
              <h3 className="text-[10px] font-bold uppercase tracking-widest text-slate-400 border-b border-slate-100 pb-1.5">
                Tags & Notes
              </h3>

              <MultiValueInput
                ref={tagsInputRef}
                label="Tags"
                values={form.tags}
                onChange={setList("tags")}
                placeholder="e.g. NCC 2022, structural, fire-safety — press Enter or +"
                testIdPrefix="tag"
              />

              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
                  Internal Notes
                </Label>
                <textarea
                  data-testid="new-project-notes"
                  placeholder="Any internal notes, key contacts, important dates…"
                  value={form.notes}
                  onChange={set("notes")}
                  rows={3}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md bg-white text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-primary resize-none"
                />
              </div>
            </section>
          </div>

          {/* ── Footer ── */}
          <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-200 flex-shrink-0">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSaving}
              data-testid="cancel-project-btn"
              className="text-slate-600"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSaving}
              data-testid="save-project-btn"
              className="bg-primary hover:bg-primary/90 text-white min-w-[9rem]"
            >
              {isSaving ? (
                <ArrowsClockwise size={14} className="mr-1.5 animate-spin" />
              ) : (
                <CheckCircle size={14} className="mr-1.5" />
              )}
              {saveLabel()}
            </Button>
          </div>
        </form>
      </div>

      {/* Source Folder Picker (for import) */}
      {showFolderPicker && (
        <SourceFolderPicker
          onSelect={(folder) => {
            setImportFolders((prev) =>
              prev.find((f) => f.id === folder.id) ? prev : [...prev, folder]
            );
            setShowFolderPicker(false);
            analyzeImportFolder(folder);
          }}
          onClose={() => setShowFolderPicker(false)}
        />
      )}
    </div>
  );
}
